import { format } from "date-fns"
import { getVideoThumbnail, inferIsVideoFromUrlOrName } from "../components/AIAssetManager/assetManagerHelpers"
import { formatPostPublishLabel } from "./utils/timezone"
import {
  catalogTypeIdFromKey,
  plannerChannelIdFromDescription,
  plannerChannelIdFromTypeId,
} from "./utils/socialNetworkCatalog"

function pickFirstUrl(...candidates) {
  for (const value of candidates) {
    if (value != null && String(value).trim() !== "") return String(value).trim()
  }
  return ""
}

function videoPreviewFromRecord(record, videoSrc) {
  if (!record || !videoSrc) return undefined
  const thumb = getVideoThumbnail(record)
  return thumb && thumb !== videoSrc ? thumb : undefined
}

/** @deprecated Prefer {@link resolvePostMediaTypeBadges} for card UI badges. */
export function formatPostMediaCountLabel(count, isVideo) {
  const n = Math.max(0, Number(count) || 0)
  if (n === 0) return ""
  return `${n} ${isVideo ? "VID" : "IMG"}`
}

/** Returns `["IMG"]`, `["VID"]`, `["IMG","VID"]`, or `[]` — no counts, hidden when empty. */
export function resolvePostMediaTypeBadges(medias) {
  if (!Array.isArray(medias) || medias.length === 0) return []
  let hasImg = false
  let hasVid = false
  for (const item of medias) {
    if (!item?.src) continue
    const isVideo =
      Boolean(item.isVideo) || inferIsVideoFromUrlOrName(item.src, item.alt)
    if (isVideo) hasVid = true
    else hasImg = true
  }
  const badges = []
  if (hasImg) badges.push("IMG")
  if (hasVid) badges.push("VID")
  return badges
}

function plannerMediaFromUrl(url, meta = {}) {
  if (!url || typeof url !== "string") return null
  const filename = meta.filename || meta.assetFilename || ""
  const isVideo =
    meta.isVideo === true ||
    inferIsVideoFromUrlOrName(url, filename, meta.mediaTypeId)
  const previewSrc =
    meta.previewSrc ?? (isVideo ? videoPreviewFromRecord(meta, url) : undefined)
  return {
    id: meta.id ?? meta.assetId ?? url,
    src: url,
    previewSrc,
    alt: meta.alt || filename || (isVideo ? "Post video" : "Post media"),
    assetId: meta.assetId,
    mediaEntityId: meta.mediaEntityId ?? null,
    isVideo,
  }
}

function plannerMediaFromMediaAsset(mediaAsset, postId, extra = {}) {
  if (!mediaAsset) return null
  const url = mediaAsset.assetUrl || mediaAsset.asset_url || null
  if (!url) return null
  const filename = mediaAsset.assetFilename || mediaAsset.asset_filename || ""
  const typeId = mediaAsset.mediaAssetType?.id ?? mediaAsset.mediaAssetTypeId ?? mediaAsset.media_type_id
  const isVideo = inferIsVideoFromUrlOrName(url, filename, typeId)
  const previewSrc = isVideo ? videoPreviewFromRecord(mediaAsset, url) : undefined
  return plannerMediaFromUrl(url, {
    id: `pipeline-media-${postId ?? mediaAsset.id}`,
    assetId: mediaAsset.id,
    mediaEntityId: extra.mediaEntityId ?? null,
    filename,
    mediaTypeId: typeId,
    alt: filename || undefined,
    previewSrc,
    isVideo,
  })
}

/** socialNetworkTypeId → CHANNEL_CONFIG id (built from GET social-network-types). */
export function socialNetworkTypeToChannelMap(socialNetworkTypes = []) {
  const map = {}
  for (const type of Array.isArray(socialNetworkTypes) ? socialNetworkTypes : []) {
    const key = plannerChannelIdFromDescription(type)
    if (key && type?.id != null) map[Number(type.id)] = key
  }
  return map
}

export const CONTENT_POST_TYPE_TO_STYLE = {
  1: "feed",
  2: "reel",
  3: "video",
  4: "story",
}

/** Lowercase API `contentPostType.description` → numeric id used by {@link CONTENT_POST_TYPE_TO_STYLE}. */
const CONTENT_POST_TYPE_DESC_TO_ID = {
  photo: 1,
  image: 1,
  feed: 1,
  carousel: 1,
  reel: 2,
  video: 3,
  story: 4,
}

export function channelIdFromSocialNetworkTypeId(socialNetworkTypeId, socialNetworkTypes = []) {
  return plannerChannelIdFromTypeId(socialNetworkTypeId, socialNetworkTypes) || "facebook"
}

/**
 * Topics / captions payloads often nest `socialNetworkType: { id, description }` without a root `socialNetworkTypeId`.
 * @param {object} post
 * @param {Array} [socialNetworkTypes]
 * @returns {number | null}
 */
export function resolveSocialNetworkTypeIdFromSourcePost(post, socialNetworkTypes = []) {
  if (!post || typeof post !== "object") return null
  const fromRoot = post.socialNetworkTypeId ?? post.social_network_type_id
  if (fromRoot != null && fromRoot !== "") {
    const n = Number(fromRoot)
    return Number.isNaN(n) ? null : n
  }
  const nested = post.socialNetworkType?.id ?? post.channel?.socialNetworkType?.id
  if (nested != null && nested !== "") {
    const n = Number(nested)
    return Number.isNaN(n) ? null : n
  }
  const desc =
    post.socialNetworkType?.description ??
    post.channel?.socialNetworkType?.description ??
    ""
  return catalogTypeIdFromKey(socialNetworkTypes, desc)
}

/**
 * Planner UI `channelId` (facebook / instagram / x / …) from a topic or captions post row.
 * @param {object} post
 * @param {Array} [socialNetworkTypes]
 * @returns {string}
 */
export function resolveChannelIdFromSourcePost(post, socialNetworkTypes = []) {
  const desc =
    post?.socialNetworkType?.description ?? post?.channel?.socialNetworkType?.description ?? ""
  const fromDesc = plannerChannelIdFromDescription(desc)
  if (fromDesc) return fromDesc
  const snId = resolveSocialNetworkTypeIdFromSourcePost(post, socialNetworkTypes)
  return plannerChannelIdFromTypeId(snId, socialNetworkTypes) || "facebook"
}

/**
 * @param {object} post
 * @returns {number}
 */
export function resolveContentPostTypeIdFromSourcePost(post) {
  if (!post || typeof post !== "object") return 1
  const fromRoot = post.contentPostTypeId ?? post.content_post_type_id
  if (fromRoot != null && fromRoot !== "") {
    const n = Number(fromRoot)
    if (!Number.isNaN(n)) return n
  }
  const nested = post.contentPostType?.id
  if (nested != null && nested !== "") {
    const n = Number(nested)
    if (!Number.isNaN(n)) return n
  }
  const desc = String(post.contentPostType?.description ?? post.contentPostType?.name ?? "")
    .trim()
    .toLowerCase()
  const fromDesc = CONTENT_POST_TYPE_DESC_TO_ID[desc]
  return fromDesc != null ? fromDesc : 1
}

export function styleKeyFromContentPostType(channelId, contentPostTypeId) {
  const style = CONTENT_POST_TYPE_TO_STYLE[contentPostTypeId] || "feed"
  return `${channelId}_${style}`
}

export function styleNameFromStyleKey(styleKey) {
  const parts = String(styleKey || "").split("_")
  return parts[parts.length - 1] || "feed"
}

/** Build lookup media_id → display object from Step 4 relevantMedia */
export function buildRelevantMediaLookup(relevantMedia) {
  const map = new Map()
  if (!Array.isArray(relevantMedia)) return map
  relevantMedia.forEach((m) => {
    const id = m?.media_id ?? m?.id
    if (id == null) return
    const src = m.asset_url || m.url
    const filename = m.asset_filename || m.system_asset_filename || ""
    const isVideo = inferIsVideoFromUrlOrName(src, filename, m.media_type_id)
    map.set(Number(id), {
      id,
      src,
      previewSrc: isVideo ? videoPreviewFromRecord(m, src) : undefined,
      alt: m.alt_text || filename || "Media",
      assetId: id,
      isVideo,
    })
  })
  return map
}

/** Resolve first media asset for post from ids + lookup + eventMediaMap fallbacks */
export function resolvePostMedia(mediaAssetIds, mediaLookup, eventMediaMapObjects, fallbackMedia) {
  const ids = Array.isArray(mediaAssetIds) ? mediaAssetIds : []
  for (let i = 0; i < ids.length; i += 1) {
    const mid = Number(ids[i])
    if (!Number.isNaN(mid) && mediaLookup.has(mid)) {
      return mediaLookup.get(mid)
    }
  }
  if (eventMediaMapObjects && typeof eventMediaMapObjects === "object") {
    const keys = Object.keys(eventMediaMapObjects)
    for (let k = 0; k < keys.length; k += 1) {
      const arr = eventMediaMapObjects[keys[k]]
      if (!Array.isArray(arr)) continue
      for (let j = 0; j < arr.length; j += 1) {
        const item = arr[j]
        const mid = typeof item === "number" ? item : item?.media_id
        if (mid != null && mediaLookup.has(Number(mid))) {
          return mediaLookup.get(Number(mid))
        }
        if (item?.asset_url) {
          return plannerMediaFromUrl(item.asset_url, {
            id: item.media_id ?? mid,
            assetId: item.media_id ?? mid,
            filename: item.asset_filename,
            alt: item.alt_text || item.asset_filename || "Media",
            previewSrc: videoPreviewFromRecord(item, item.asset_url),
          })
        }
      }
    }
  }
  return fallbackMedia || null
}

/**
 * Map /api/ai/captions (or regenerate) response groups to planner generatedEventGroups shape.
 * @param {Array} data - response.data
 * @param {Map} eventMetaBySourceId - sourceEventId -> { uiEventId, title, type, date, pillarName, contentPillarId }
 * @param {Map} mediaLookup - from relevantMedia
 * @param {object} eventMediaMapFull - optional full eventMediaMap from step 4 for URL fallbacks
 */
/** Numeric content topic post id for pipeline PATCH and POST …/regenerate. */
export function getContentTopicPostServerId(post) {
  if (!post) return null
  const fromField = post.contentTopicPostId
  if (fromField != null && fromField !== "") {
    const n = Number(fromField)
    return Number.isNaN(n) ? null : n
  }
  const idStr = String(post.id || "")
  if (idStr.startsWith("pipeline-post-")) {
    const n = Number(idStr.slice("pipeline-post-".length))
    return Number.isNaN(n) ? null : n
  }
  return null
}

function captionsMapFromRegenerateApiResponse(response) {
  const raw = response?.data ?? response?.posts ?? response
  const list = Array.isArray(raw) ? raw : []
  const map = new Map()
  list.forEach((item) => {
    const id = Number(item?.id ?? item?.contentTopicPostId ?? item?.content_topic_post_id)
    if (Number.isNaN(id)) return
    const text = item?.postText ?? item?.caption ?? item?.text ?? item?.description
    if (text != null && String(text).trim() !== "") map.set(id, String(text))
  })
  return map
}

/**
 * Merge POST posts/clients/:id/regenerate response into planner groups (caption text only).
 */
export function mergePipelineRegenerateResponseIntoGroups(generatedEventGroups, apiResponse, selectedPostIds) {
  const sel = selectedPostIds instanceof Set ? selectedPostIds : new Set(selectedPostIds)
  const captions = captionsMapFromRegenerateApiResponse(apiResponse)
  if (captions.size === 0) return generatedEventGroups
  return generatedEventGroups.map((group) => ({
    ...group,
    posts: group.posts.map((p) => {
      if (!sel.has(p.id)) return p
      const sid = getContentTopicPostServerId(p)
      if (sid == null) return p
      const next = captions.get(sid)
      if (next === undefined) return p
      return { ...p, text: next }
    }),
  }))
}

/** Merge Step 6 caption response into existing groups; updates only posts whose id is in selectedPostIds */
export function mergeRegeneratedCaptionsIntoGroups(generatedEventGroups, responseData, selectedPostIds) {
  const sel = selectedPostIds instanceof Set ? selectedPostIds : new Set(selectedPostIds)
  const queues = {}
  const data = responseData?.data || responseData
  if (!Array.isArray(data)) return generatedEventGroups
  data.forEach((g) => {
    if (g?.eventId != null) {
      queues[String(g.eventId)] = [...(g.posts || [])]
    }
  })

  return generatedEventGroups.map((group) => {
    const sid = String(group.sourceEventId ?? "")
    const queue = [...(queues[sid] || [])]
    return {
      ...group,
      posts: group.posts.map((p) => {
        if (!sel.has(p.id)) return p
        const np = queue.shift()
        if (!np) return p
        return {
          ...p,
          text: np.postText ?? p.text,
          postDatetime: np.postDatetime ?? p.postDatetime,
        }
      }),
    }
  })
}

export function mapCaptionsResponseToEventGroups(
  data,
  eventMetaBySourceId,
  mediaLookup,
  eventMediaMapFull,
  socialNetworkTypes = [],
) {
  if (!Array.isArray(data)) return []
  return data.map((group, groupIndex) => {
    const eventId = String(group.eventId ?? "")
    const meta = eventMetaBySourceId.get(eventId) || {}
    const uiEventId = meta.uiEventId || `api-event-${eventId || groupIndex}`
    const eventDate = meta.date instanceof Date ? meta.date : new Date(meta.date || Date.now())
    const posts = (group.posts || []).map((p, i) => {
      const channelId = resolveChannelIdFromSourcePost(p, socialNetworkTypes)
      const cptId = resolveContentPostTypeIdFromSourcePost(p)
      const styleKey = styleKeyFromContentPostType(channelId, cptId)
      const medias = (Array.isArray(p.mediaAssetIds) ? p.mediaAssetIds : [])
        .map((id) => {
          const mid = Number(id)
          return !Number.isNaN(mid) && mediaLookup.has(mid) ? mediaLookup.get(mid) : null
        })
        .filter(Boolean)
      const media =
        medias[0] ||
        resolvePostMedia(p.mediaAssetIds, mediaLookup, eventMediaMapFull, null)
      const mediaTypeBadges = resolvePostMediaTypeBadges(
        medias.length > 0 ? medias : media ? [media] : [],
      )
      return {
        id: `${uiEventId}-post-${i}`,
        title: group.name || meta.title || "Post",
        text: p.postText || "",
        styleKey,
        channelId,
        mediaTypeBadges,
        media,
        publishLabel: formatPostPublishLabel(p.postDatetime, eventDate),
        contentPillarName: meta.pillarName || "",
        contentPillarId: group.contentPillarId ?? meta.contentPillarId,
        mediaAssetIds: p.mediaAssetIds || [],
        eventId,
        eventDate: format(eventDate, "yyyy-MM-dd"),
        postDatetime: p.postDatetime,
        socialNetworkTypeId: resolveSocialNetworkTypeIdFromSourcePost(p, socialNetworkTypes) ?? p.socialNetworkTypeId ?? null,
        contentPostTypeId: cptId,
        contentPostStatusId: p.contentPostStatusId ?? 1,
      }
    })
    return {
      id: uiEventId,
      sourceEventId: eventId,
      title: group.name || meta.title || "Event",
      type: meta.type || "event",
      date: eventDate,
      posts,
    }
  })
}

/** Serializable meta for {@link buildEventMetaMapFromEligible} (from planner UI before generate). */
export function buildEligibleMetaForSession(eligible, selectedDisplayPillars) {
  return eligible.map((ev, idx) => {
    const pillar = selectedDisplayPillars[idx % Math.max(selectedDisplayPillars.length, 1)]
    const pillarName = pillar?.contentPillar?.name || pillar?.contentPillar?.description || ""
    const rawPillarId = pillar?.contentPillar?.id ?? pillar?.id
    const contentPillarId =
      rawPillarId != null && !String(rawPillarId).startsWith("local-") ? Number(rawPillarId) : undefined
    const d = ev.date instanceof Date ? ev.date : new Date(ev.date)
    return {
      uiEventId: ev.id,
      sourceEventId: String(ev.sourceEventId || ev.id),
      title: ev.title,
      type: ev.type,
      dateIso: d.toISOString(),
      pillarName,
      contentPillarId,
    }
  })
}

export function buildEventMetaMapFromEligible(eligibleMeta) {
  const eventMetaBySourceId = new Map()
  if (!Array.isArray(eligibleMeta)) return eventMetaBySourceId
  eligibleMeta.forEach((meta) => {
    eventMetaBySourceId.set(String(meta.sourceEventId), {
      uiEventId: meta.uiEventId,
      title: meta.title,
      type: meta.type,
      date: new Date(meta.dateIso),
      pillarName: meta.pillarName,
      contentPillarId: meta.contentPillarId,
    })
  })
  return eventMetaBySourceId
}

export function serializeGeneratedGroupsForSession(groups) {
  if (!Array.isArray(groups)) return []
  return groups.map((g) => {
    const { date, ...rest } = g
    return {
      ...rest,
      dateISO: date instanceof Date ? date.toISOString() : date ? new Date(date).toISOString() : null,
    }
  })
}

/** Same shape as {@link saveAIPlannerSession} payload (Redux). */
export function buildAIPlannerSessionSavePayload(mappedGroups, sessionContext) {
  const serialized = serializeGeneratedGroupsForSession(mappedGroups)
  const mediaFromPosts = mappedGroups.flatMap((g) => g.posts?.map((p) => p.media) || []).filter(Boolean)
  const seenMediaIds = new Set()
  const uniqueMedia = mediaFromPosts.filter((m) => m?.id && !seenMediaIds.has(m.id) && (seenMediaIds.add(m.id), true))
  return {
    clientId: sessionContext.clientId,
    dateRange: sessionContext.dateRange,
    generatedEventGroups: serialized,
    scheduledGeneratedPostIds: [...(sessionContext.scheduledGeneratedPostIds || [])],
    draftGeneratedPostIds: [...(sessionContext.draftGeneratedPostIds || [])],
    selectedEventIds: [...(sessionContext.selectedEventIds || [])],
    selectedPillarIds: [...(sessionContext.selectedPillarIds || [])],
    selectedChannelIds: [...(sessionContext.selectedChannelIds || [])],
    selectedPublishingStyleIds: [...(sessionContext.selectedPublishingStyleIds || [])],
    mediaAssets: uniqueMedia.length > 0 ? uniqueMedia : sessionContext.mediaAssets || [],
  }
}

function postMatchesPipelineStatusZero(post) {
  const sid = post?.contentPostStatus?.id ?? post?.contentPostStatusId
  return sid === 0 || sid === "0"
}

/** Numeric content post status (0 = pipeline / draft queue, 1 draft, 6 scheduled, …). */
export function getContentPostStatusIdNumber(post) {
  const raw = post?.contentPostStatus?.id ?? post?.contentPostStatusId
  const n = typeof raw === "number" ? raw : Number(raw)
  return Number.isNaN(n) ? NaN : n
}

/** UTC ms from API for post row insert/create/update (used to drop stale draft/scheduled from older runs). */
export function getPipelinePostInsertedTimeMs(post) {
  if (!post || typeof post !== "object") return NaN
  const candidates = [
    post.insertedDatetime,
    post.inserted_datetime,
    post.insertedAt,
    post.inserted_at,
    post.createdAt,
    post.created_at,
    post.updatedAt,
    post.updated_at,
    post.modifyAt,
    post.modify_at,
    post.lastModifiedAt,
    post.last_modified_at,
    post.contentTopicPost?.insertedDatetime,
    post.contentTopicPost?.inserted_datetime,
    post.contentTopicPost?.insertedAt,
    post.contentTopicPost?.inserted_at,
    post.contentTopicPost?.createdAt,
    post.contentTopicPost?.created_at,
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i]
    if (c == null || c === "") continue
    const d = c instanceof Date ? c : new Date(c)
    const t = d.getTime()
    if (!Number.isNaN(t)) return t
  }
  return NaN
}

function getContentTopicInsertedTimeMs(topic) {
  if (!topic || typeof topic !== "object") return NaN
  const ct = topic.contentTopic || {}
  const candidates = [
    topic.insertedDatetime,
    topic.inserted_datetime,
    topic.insertedAt,
    topic.inserted_at,
    topic.createdAt,
    topic.created_at,
    ct.insertedDatetime,
    ct.inserted_datetime,
    ct.insertedAt,
    ct.inserted_at,
    ct.createdAt,
    ct.created_at,
    ct.updatedAt,
    ct.updated_at,
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i]
    if (c == null || c === "") continue
    const d = c instanceof Date ? c : new Date(c)
    const t = d.getTime()
    if (!Number.isNaN(t)) return t
  }
  return NaN
}

/** Clock skew / ordering slack when comparing status 0 vs draft/scheduled insert times. */
const AI_PLANNER_INSERT_SLACK_MS = 5 * 60 * 1000

function isAiPlannerDebugEnabled() {
  try {
    return process.env.NODE_ENV === "development" || Boolean(window?.__AI_PLANNER_DEBUG)
  } catch {
    return process.env.NODE_ENV === "development"
  }
}

function aiPlannerDebugLog(message, payload) {
  if (!isAiPlannerDebugEnabled()) return
  // eslint-disable-next-line no-console
  console.debug(`[AI Planner][pipeline] ${message}`, payload)
}

/**
 * Keeps draft (1) / scheduled (6) AI posts only if their insert time is not before the latest status-0
 * AI post on this topic (or the content topic’s own insert time if there is no status 0).
 */
function filterAiTopicPostsByInsertTime(topic, rawPosts) {
  const posts = rawPosts || []
  const ai = posts.filter((p) => isTopicPostInAiPlannerPipeline(p))
  if (ai.length === 0) return posts

  const statusZeroTimes = ai
    .filter((p) => getContentPostStatusIdNumber(p) === 0)
    .map(getPipelinePostInsertedTimeMs)
    .filter((t) => !Number.isNaN(t))

  const topicCreatedMs = getContentTopicInsertedTimeMs(topic)

  let cutoffMs
  if (statusZeroTimes.length > 0) {
    cutoffMs = Math.max(...statusZeroTimes) - AI_PLANNER_INSERT_SLACK_MS
  } else if (!Number.isNaN(topicCreatedMs)) {
    cutoffMs = topicCreatedMs - AI_PLANNER_INSERT_SLACK_MS
  } else {
    return posts
  }

  return posts.filter((p) => {
    const sid = getContentPostStatusIdNumber(p)
    if (sid === 0) return true
    if (sid !== 1 && sid !== 6) return true
    if (!isTopicPostInAiPlannerPipeline(p)) return true
    const t = getPipelinePostInsertedTimeMs(p)
    if (Number.isNaN(t)) return true
    return t >= cutoffMs
  })
}

function getTopicLatestAiInsertedMs(topic) {
  const posts = topic?.contentTopicPosts || []
  const times = posts
    .filter((p) => isTopicPostInAiPlannerPipeline(p))
    .map(getPipelinePostInsertedTimeMs)
    .filter((t) => !Number.isNaN(t))
  if (times.length === 0) return NaN
  return Math.max(...times)
}

/**
 * “Run” time for a content topic: prefer {@link getContentTopicInsertedTimeMs} (content topic row),
 * else max AI post time on the topic. Used to keep every topic from one generate (e.g. 3 status-0 rows).
 */
function getTopicRunTimeMs(topic) {
  const ct = getContentTopicInsertedTimeMs(topic)
  if (!Number.isNaN(ct)) return ct
  return getTopicLatestAiInsertedMs(topic)
}

/**
 * All topics in one generate usually fall within a few minutes of each other. Older full runs (hours/days) fall outside.
 */
const AI_LATEST_RUN_BATCH_MAX_SPREAD_MS = 4 * 60 * 1000

/**
 * Keep all content topics that belong to the **latest** AI batch (by inserted time), not just one row.
 * One generate can create multiple topics (e.g. 3 events with status 0) with slightly different `insertedDatetime`;
 * we keep any topic whose run time is within {@link AI_LATEST_RUN_BATCH_MAX_SPREAD_MS} of the max time.
 * After refresh, draft/scheduled on those topics still map per-topic; this only trims **older** generations.
 */
function keepLatestAiRunTopics(topicsList) {
  if (!Array.isArray(topicsList) || topicsList.length <= 1) return topicsList
  const withTimes = topicsList.map((topic) => ({ topic, tMs: getTopicRunTimeMs(topic) }))
  const valid = withTimes.filter((x) => !Number.isNaN(x.tMs))
  if (valid.length === 0) return topicsList
  const maxT = Math.max(...valid.map((x) => x.tMs))
  const minKeepT = maxT - AI_LATEST_RUN_BATCH_MAX_SPREAD_MS
  const kept = valid.filter((x) => x.tMs >= minKeepT && x.tMs <= maxT).map((x) => x.topic)
  if (kept.length === 0) return topicsList
  aiPlannerDebugLog("latest-ai-run-topics", {
    maxRunTimeMs: maxT,
    minKeepTimeMs: minKeepT,
    batchSpreadMs: AI_LATEST_RUN_BATCH_MAX_SPREAD_MS,
    keptTopicIds: kept.map((t) => t?.contentTopic?.id ?? t?.id),
    droppedTopicIds: topicsList
      .map((t) => t?.contentTopic?.id ?? t?.id)
      .filter((id) => !kept.some((k) => (k?.contentTopic?.id ?? k?.id) === id)),
  })
  return kept
}

function postMatchesAllowedAiPlannerStatuses(post, allowed) {
  if (!Array.isArray(allowed) || allowed.length === 0) return postStatusInAiPlannerFetchWindow(post)
  const sid = getContentPostStatusIdNumber(post)
  return allowed.some((a) => Number(a) === sid)
}

/** Start-over DELETE: only when API sends boolean `true`, not truthy strings/numbers. */
function isAiGeneratedStrictBooleanTrue(post) {
  return post?.aiGenerated === true || post?.ai_generated === true
}

/** AI Planner list: only server-flagged AI posts in draft (1), AI pipeline (0), or scheduled (6). */
function isAiGeneratedPipelinePost(post) {
  return post?.aiGenerated === true || post?.ai_generated === true
}

/** When both are explicitly false, the row is a normal (non–AI) calendar post. */
function isExplicitNonAiTopicPost(post) {
  return post?.aiGenerated === false || post?.ai_generated === false
}

/**
 * GET /topics row should appear in AI Planner when mapping. Status 0 still requires the AI boolean.
 * Status 1 / 6 are included even if the API cleared `aiGenerated` after the user saved draft/scheduled in
 * the campaign drawer — otherwise refetch never merges the new status and badges stay on “not draft”.
 * Merge uses {@link filterIncomingPlannerPostsForSessionMerge} to drop unknown 1/6 so unrelated drafts are not added.
 */
function isTopicPostInAiPlannerPipeline(post) {
  if (isExplicitNonAiTopicPost(post)) return false
  if (isAiGeneratedPipelinePost(post)) return true
  const sid = getContentPostStatusIdNumber(post)
  if (sid === 1 || sid === 6) return true
  return false
}

function postStatusInAiPlannerFetchWindow(post) {
  const sid = Number(post?.contentPostStatus?.id ?? post?.contentPostStatusId)
  return sid === 0 || sid === 1 || sid === 6
}

/** `contentTopicPosts[].id` for status-0 rows (any post, including non-AI). */
export function collectStatusZeroPipelinePostIds(topicsList) {
  const seen = new Set()
  const ids = []
  if (!Array.isArray(topicsList)) return ids
  for (const topic of topicsList) {
    for (const p of topic.contentTopicPosts || []) {
      if (!postMatchesPipelineStatusZero(p)) continue
      const pid = p?.id
      if (pid == null) continue
      const key = String(pid)
      if (seen.has(key)) continue
      seen.add(key)
      const n = typeof pid === "number" ? pid : Number(pid)
      if (!Number.isNaN(n)) ids.push(n)
    }
  }
  return ids
}

/** Status-0 row seeds start-over delete unless API explicitly marks it non–AI-generated. */
function isStartOverSeedPost(p) {
  if (getContentPostStatusIdNumber(p) !== 0) return false
  if (isExplicitNonAiTopicPost(p)) return false
  return true
}

/**
 * Post ids for AI Planner “Start over” `DELETE /posts` (JSON body array).
 * Seeds: pipeline **status 0** rows that are not explicitly `aiGenerated: false`. Collects each seed’s
 * {@link getPipelinePostInsertedTimeMs}, then adds **every** topic post whose insert time matches **any** seed time,
 * so one generate (same `insertedDatetime`) deletes together. **Never** includes draft (**1**) or scheduled (**6**).
 * Status-0 rows without a parseable insert time are still deleted via the seed id set.
 */
export function collectAiPlannerStartOverPostIds(topicsList) {
  const seen = new Set()
  const ids = []
  if (!Array.isArray(topicsList)) return ids

  const seedInsertMs = new Set()
  const seedPostIdKeys = new Set()

  for (const topic of topicsList) {
    for (const p of topic.contentTopicPosts || []) {
      if (!isStartOverSeedPost(p)) continue
      const pid = p?.id
      if (pid == null) continue
      seedPostIdKeys.add(String(pid))
      const t = getPipelinePostInsertedTimeMs(p)
      if (!Number.isNaN(t)) seedInsertMs.add(t)
    }
  }

  for (const topic of topicsList) {
    for (const p of topic.contentTopicPosts || []) {
      const sid = getContentPostStatusIdNumber(p)
      if (sid === 1 || sid === 6) continue

      const pid = p?.id
      if (pid == null) continue
      const key = String(pid)
      if (seen.has(key)) continue

      const t = getPipelinePostInsertedTimeMs(p)
      const inSeedBatchByTime =
        seedInsertMs.size > 0 && !Number.isNaN(t) && seedInsertMs.has(t)
      const isSeedPost = seedPostIdKeys.has(key)

      if (!inSeedBatchByTime && !isSeedPost) continue

      seen.add(key)
      const n = typeof pid === "number" ? pid : Number(pid)
      if (!Number.isNaN(n)) ids.push(n)
    }
  }
  return ids
}

function getTopicNumericIdForMerge(topic) {
  const tid = topic?.contentTopic?.id ?? topic?.id
  const n = typeof tid === "number" ? tid : Number(tid)
  return Number.isNaN(n) ? 0 : n
}

/** Same calendar event across repeated AI runs (multiple content topic ids). */
export function getAiPlannerEventIdentityKey(topic) {
  const ev = topic?.event || {}
  if (ev.eventId != null && String(ev.eventId).trim() !== "") {
    return String(ev.eventId)
  }
  if (ev.id != null && String(ev.id).trim() !== "") {
    return String(ev.id)
  }
  return `topic-${getTopicNumericIdForMerge(topic)}`
}

/** Match UI `sourceEventId` values (`2462`) with legacy pipeline keys (`event-numeric-2462`). */
export function plannerSourceEventIdsMatch(a, b) {
  const sa = String(a ?? "").trim()
  const sb = String(b ?? "").trim()
  if (!sa || !sb) return false
  if (sa === sb) return true
  const na = sa.replace(/^event-numeric-/, "")
  const nb = sb.replace(/^event-numeric-/, "")
  return na !== sa && nb !== sb && na === nb
}

/**
 * API can return multiple topics for the same event across different AI runs.
 * Keep only the newest topic (highest content topic id) so older run drafts/scheduled
 * posts never reappear in AI Planner after refresh/refetch.
 */
export function mergeDuplicateEventTopicsForAiPlannerPipeline(topicsList) {
  if (!Array.isArray(topicsList) || topicsList.length <= 1) return topicsList

  const byKey = new Map()
  for (const topic of topicsList) {
    const key = getAiPlannerEventIdentityKey(topic)
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(topic)
  }

  let anyDup = false
  for (const g of byKey.values()) {
    if (g.length > 1) {
      anyDup = true
      break
    }
  }
  if (!anyDup) return topicsList

  const processed = new Set()
  const out = []

  for (const topic of topicsList) {
    const key = getAiPlannerEventIdentityKey(topic)
    const group = byKey.get(key)
    if (group.length === 1) {
      out.push(topic)
      continue
    }
    if (processed.has(key)) continue
    processed.add(key)

    const maxTid = Math.max(...group.map(getTopicNumericIdForMerge))
    const winnerTopic =
      group.find((t) => getTopicNumericIdForMerge(t) === maxTid) || group[group.length - 1]

    const mergedPosts = []
    const seenPostIds = new Set()
    group
      .slice()
      .sort((a, b) => getTopicNumericIdForMerge(b) - getTopicNumericIdForMerge(a))
      .forEach((t) => {
        ;(t.contentTopicPosts || []).forEach((p) => {
          if (p?.id == null) return
          const pid = String(p.id)
          if (seenPostIds.has(pid)) return
          seenPostIds.add(pid)
          mergedPosts.push(p)
        })
      })
    const keptPosts = mergedPosts.filter((p) => isTopicPostInAiPlannerPipeline(p))

    aiPlannerDebugLog("dedupe-event-topic", {
      eventKey: key,
      topicIds: group.map((t) => getTopicNumericIdForMerge(t)),
      selectedTopicId: maxTid,
      selectedPostIds: keptPosts.map((p) => p?.id).filter((id) => id != null),
      mergedFromTopicIds: group.map((t) => getTopicNumericIdForMerge(t)),
    })

    out.push({
      ...winnerTopic,
      id: maxTid,
      contentTopic: {
        ...(winnerTopic.contentTopic || {}),
        id: maxTid,
      },
      contentTopicPosts: keptPosts,
    })
  }

  return out
}

/**
 * Map GET /api/v1/topics/:clientId?post_status_ids=…&start_date&end_date into planner event groups.
 * Includes AI pipeline posts; draft/scheduled (1/6) are still included if `aiGenerated` was cleared on save
 * (see {@link isTopicPostInAiPlannerPipeline}). Unrelated 1/6 are filtered in merge.
 * @param {Array} topicsList
 * @param {{ allowedPostStatusIds?: number[], applyInsertTimeFilter?: boolean }} [options] — when set (e.g. `[0]`), only those statuses are mapped (matches query). `applyInsertTimeFilter` (default true when statuses include 1 or 6): drop older-topic draft/scheduled using API insert/create times vs latest status 0 on the topic.
 */
export function mapPipelineTopicsToPlannerGroups(topicsList, options = {}) {
  if (!Array.isArray(topicsList)) return []
  const allowed = options.allowedPostStatusIds
  const isZeroOnlyRefresh =
    Array.isArray(allowed) && allowed.length === 1 && Number(allowed[0]) === 0
  const applyInsertTimeFilter =
    options.applyInsertTimeFilter !== false &&
    !isZeroOnlyRefresh &&
    (!Array.isArray(allowed) || allowed.length === 0 || allowed.some((s) => Number(s) === 1 || Number(s) === 6))
  const latestRunTopics = keepLatestAiRunTopics(topicsList)
  const dedupedTopics = mergeDuplicateEventTopicsForAiPlannerPipeline(latestRunTopics)
  const out = []
  dedupedTopics.forEach((topic) => {
    let posts = (topic.contentTopicPosts || []).filter(
      (p) => isTopicPostInAiPlannerPipeline(p) && postMatchesAllowedAiPlannerStatuses(p, allowed),
    )
    if (applyInsertTimeFilter) {
      posts = filterAiTopicPostsByInsertTime(topic, posts)
    }
    if (posts.length === 0) return
    aiPlannerDebugLog("map-topic-posts", {
      topicId: topic?.contentTopic?.id ?? topic?.id,
      sourceEventId: getAiPlannerEventIdentityKey(topic),
      allowedStatuses: Array.isArray(allowed) ? allowed : [0, 1, 6],
      keptStatusCounts: posts.reduce((acc, p) => {
        const sid = Number(getContentPostStatusIdNumber(p))
        const key = Number.isNaN(sid) ? "unknown" : String(sid)
        acc[key] = (acc[key] || 0) + 1
        return acc
      }, {}),
    })
    const event = topic.event || {}
    const eventKey = getAiPlannerEventIdentityKey(topic)
    const eventDate = event.date ? new Date(event.date) : new Date()
    /** Match checklist labels (`name`); description-first broke pillar filter vs dropdown. */
    const pillarName = topic.contentPillar?.name || topic.contentPillar?.description || ""

    const topicNumericId = topic?.contentTopic?.id ?? topic?.id

    const mappedPosts = posts.map((post) => {
      const snId = resolveSocialNetworkTypeIdFromSourcePost(post, options.socialNetworkTypes)
      const channelId = resolveChannelIdFromSourcePost(post, options.socialNetworkTypes)
      const cptId = resolveContentPostTypeIdFromSourcePost(post)
      const styleKey = styleKeyFromContentPostType(channelId, cptId)
      const ctpMedia = post.contentTopicPostMedias?.[0]
      const mediaAsset = ctpMedia?.mediaAsset
      const allMedias = post.contentTopicPostMedias || []
      const medias = allMedias
        .map((row) => {
          const asset = row?.mediaAsset
          const parentMediaId = row?.media?.id ?? row?.mediaId ?? row?.media_id ?? null
          const fallbackUrl =
            asset?.assetUrl ||
            row?.media?.mediaAssets?.[0]?.assetUrl ||
            null
          return (
            plannerMediaFromMediaAsset(asset, post.id, { mediaEntityId: parentMediaId }) ||
            (fallbackUrl
              ? plannerMediaFromUrl(fallbackUrl, {
                  id: `pipeline-media-${post.id}`,
                  assetId: asset?.id,
                  mediaEntityId: parentMediaId,
                  alt: "Post media",
                })
              : null)
          )
        })
        .filter((item) => item?.src)
      const fallbackUrl =
        mediaAsset?.assetUrl ||
        ctpMedia?.media?.mediaAssets?.[0]?.assetUrl ||
        null
      const media = medias[0] ||
        plannerMediaFromMediaAsset(mediaAsset, post.id, {
          mediaEntityId: ctpMedia?.media?.id ?? ctpMedia?.mediaId ?? null,
        }) ||
        (fallbackUrl
          ? plannerMediaFromUrl(fallbackUrl, {
              id: `pipeline-media-${post.id}`,
              assetId: mediaAsset?.id,
              mediaEntityId: ctpMedia?.media?.id ?? ctpMedia?.mediaId ?? null,
              alt: "Post media",
            })
          : null)
      const mediaTypeBadges = resolvePostMediaTypeBadges(
        medias.length > 0 ? medias : media ? [media] : [],
      )
      const statusId = Number(post.contentPostStatus?.id ?? post.contentPostStatusId ?? 0)
      return {
        id: `pipeline-post-${post.id}`,
        title: topic.name || event.name || "Post",
        text: post.postText || post.text || "",
        styleKey,
        channelId,
        mediaTypeBadges,
        media,
        publishLabel: formatPostPublishLabel(post.postDatetime, eventDate),
        contentPillarName: pillarName,
        contentPillarId: topic.contentPillar?.id ?? null,
        contentTopicPostId: post.id,
        contentTopicId: topicNumericId != null ? Number(topicNumericId) || topicNumericId : null,
        postDatetime: post.postDatetime || null,
        socialNetworkTypeId: snId,
        contentPostTypeId: cptId,
        contentPostStatusId: Number.isNaN(statusId) ? 0 : statusId,
        aiGenerated: Boolean(post.aiGenerated ?? post.ai_generated),
        contentTopicPostMedias: allMedias,
      }
    })

    out.push({
      id: `pipeline-topic-${topic.id}`,
      sourceEventId: eventKey,
      title: event.name || topic.name || "Campaign",
      type: String(event.category || "event").toLowerCase(),
      date: eventDate,
      posts: mappedPosts,
    })
  })
  return out
}

function isPipelinePlannerPostId(id) {
  return String(id || "").startsWith("pipeline-post-")
}

function isPipelineTopicGroupId(id) {
  return String(id || "").startsWith("pipeline-topic-")
}

/**
 * When GET topics (status 0) returns [], drop server-synced pipeline groups/posts so the planner matches the API.
 */
export function stripPipelineSyncedPostsFromPersistedSavePayload(persisted, clientId, dateRange) {
  if (!clientId) return null

  const deserializeGroup = (g) => ({
    ...g,
    date: g.dateISO ? new Date(g.dateISO) : g.date ? new Date(g.date) : new Date(),
  })

  const existing =
    persisted?.clientId === clientId && Array.isArray(persisted.generatedEventGroups)
      ? persisted.generatedEventGroups.map(deserializeGroup)
      : []

  let changed = false
  const next = []
  existing.forEach((g) => {
    if (isPipelineTopicGroupId(g.id)) {
      changed = true
      return
    }
    const prevPosts = g.posts || []
    const posts = prevPosts.filter((p) => {
      if (isPipelinePlannerPostId(p?.id)) {
        changed = true
        return false
      }
      return true
    })
    if (posts.length === 0) {
      if (prevPosts.length > 0) changed = true
      return
    }
    next.push({ ...g, posts })
  })

  if (!changed) return null

  const dateR =
    Array.isArray(dateRange) && dateRange.length === 2
      ? dateRange
      : persisted?.dateRange?.length === 2 && persisted.clientId === clientId
        ? persisted.dateRange
        : dateRange

  return {
    clientId,
    dateRange: dateR,
    generatedEventGroups: serializeGeneratedGroupsForSession(next),
    scheduledGeneratedPostIds: [...(persisted?.scheduledGeneratedPostIds || [])],
    draftGeneratedPostIds: [...(persisted?.draftGeneratedPostIds || [])],
    selectedEventIds: [...(persisted?.selectedEventIds || [])],
    selectedPillarIds: [...(persisted?.selectedPillarIds || [])],
    selectedChannelIds: [...(persisted?.selectedChannelIds || [])],
    selectedPublishingStyleIds: [...(persisted?.selectedPublishingStyleIds || [])],
    mediaAssets: persisted?.mediaAssets || [],
  }
}

/**
 * When refetching 0+1+6, ignore draft/scheduled rows from the API unless they match a post already in this planner session.
 * Stops unrelated historical AI drafts/schedules reappearing after scheduling one post.
 */
function filterIncomingPlannerPostsForSessionMerge(posts, restrict, knownServerIds) {
  if (!restrict || !(knownServerIds instanceof Set)) return posts || []
  if (knownServerIds.size === 0) {
    return (posts || []).filter((p) => {
      const st = Number(p?.contentPostStatusId ?? p?.contentPostStatus?.id ?? NaN)
      return st === 0 || Number.isNaN(st)
    })
  }
  return (posts || []).filter((p) => {
    const st = Number(p?.contentPostStatusId ?? p?.contentPostStatus?.id ?? NaN)
    if (st === 0) return true
    if (Number.isNaN(st)) return true
    if (st === 1 || st === 6) {
      const sid = getContentTopicPostServerId(p)
      return sid != null && knownServerIds.has(sid)
    }
    return true
  })
}

function collectKnownTopicPostServerIdsFromGroups(groups) {
  const ids = new Set()
  ;(groups || []).forEach((g) => {
    ;(g?.posts || []).forEach((p) => {
      const sid = getContentTopicPostServerId(p)
      if (sid != null) ids.add(sid)
    })
  })
  return ids
}

/**
 * After merging API posts into a Map, `Array.from(map.values())` can reorder cards (e.g. delete+set moves a key
 * to the end). Keep the grid order stable by walking the previous session order first, then append new rows.
 */
function stabilizeMergedPlannerPostsOrder(prevPosts, byPostId, incomingPosts) {
  const used = new Set()
  const out = []

  const pickForPrevSlot = (prev) => {
    const id = String(prev.id)
    if (byPostId.has(id)) return byPostId.get(id)
    const sid = getContentTopicPostServerId(prev)
    if (sid == null) return undefined
    for (const p of byPostId.values()) {
      if (getContentTopicPostServerId(p) === sid) return p
    }
    return undefined
  }

  for (const prev of prevPosts || []) {
    const next = pickForPrevSlot(prev)
    if (!next) continue
    const uid = String(next.id)
    if (used.has(uid)) continue
    used.add(uid)
    out.push(next)
  }
  for (const inc of incomingPosts || []) {
    const uid = String(inc.id)
    if (used.has(uid)) continue
    if (!byPostId.has(uid)) continue
    used.add(uid)
    out.push(byPostId.get(uid))
  }
  return out
}

/**
 * Merge pipeline topics (status 0) into persisted AI Planner session without dropping existing groups.
 * @param {{ replacePipelinePostsInMatchedGroups?: boolean, pruneStalePipelineGroups?: boolean, restrictNonZeroToKnownServerIds?: boolean }} [mergeOpts] — if true, drop prior `pipeline-post-*` rows in matched groups before merging (new AI run / start over). `pruneStalePipelineGroups`: remove `pipeline-topic-*` groups whose event is absent from this response (used after status-0-only fetch so old sessions’ 1/6 topics disappear). `restrictNonZeroToKnownServerIds`: after a 0+1+6 fetch, only merge draft(1)/scheduled(6) for topic-post ids already present in the session (still merge all status 0).
 */
export function mergePipelineTopicsIntoSavePayload(
  persisted,
  pipelineGroups,
  {
    clientId,
    dateRange,
    replacePipelinePostsInMatchedGroups = false,
    pruneStalePipelineGroups = false,
    restrictNonZeroToKnownServerIds = false,
  },
) {
  if (!pipelineGroups?.length || !clientId) return null

  const deserializeGroup = (g) => ({
    ...g,
    date: g.dateISO ? new Date(g.dateISO) : g.date ? new Date(g.date) : new Date(),
  })

  const existing =
    persisted?.clientId === clientId && Array.isArray(persisted.generatedEventGroups)
      ? persisted.generatedEventGroups.map(deserializeGroup)
      : []

  const knownServerIds = collectKnownTopicPostServerIdsFromGroups(existing)

  const merged = [...existing]
  pipelineGroups.forEach((pg) => {
    const incomingPosts = filterIncomingPlannerPostsForSessionMerge(
      pg.posts,
      restrictNonZeroToKnownServerIds,
      knownServerIds,
    )
    let i = merged.findIndex((g) => g.id === pg.id)
    if (
      i === -1 &&
      pg?.sourceEventId != null &&
      String(pg.sourceEventId).trim() !== "" &&
      isPipelineTopicGroupId(pg?.id)
    ) {
      i = merged.findIndex(
        (g) =>
          isPipelineTopicGroupId(g?.id) &&
          plannerSourceEventIdsMatch(g?.sourceEventId, pg.sourceEventId),
      )
    }
    if (i === -1 && pg?.sourceEventId != null && String(pg.sourceEventId).trim() !== "") {
      i = merged.findIndex((g) => plannerSourceEventIdsMatch(g?.sourceEventId, pg.sourceEventId))
    }
    if (i === -1) {
      if (incomingPosts.length === 0) return
      merged.push({ ...pg, posts: [...incomingPosts] })
    } else {
      const cur = merged[i]
      const incomingServerIds = new Set(
        incomingPosts
          .map((p) => getContentTopicPostServerId(p))
          .filter((id) => id != null),
      )
      const basePosts =
        replacePipelinePostsInMatchedGroups
          ? (cur.posts || []).filter((p) => {
              if (isPipelinePlannerPostId(p?.id)) return false
              const sid = getContentTopicPostServerId(p)
              return sid == null || !incomingServerIds.has(sid)
            })
          : [...(cur.posts || [])]
      const byPostId = new Map(basePosts.map((p) => [String(p.id), p]))
      incomingPosts.forEach((p) => {
        const incomingServerId = getContentTopicPostServerId(p)
        if (incomingServerId != null) {
          for (const [existingId, existingPost] of byPostId.entries()) {
            if (String(p.id) === existingId) continue
            if (getContentTopicPostServerId(existingPost) === incomingServerId) {
              byPostId.delete(existingId)
            }
          }
        }
        byPostId.set(String(p.id), p)
      })
      merged[i] = {
        ...cur,
        id: pg.id,
        sourceEventId: pg.sourceEventId,
        title: pg.title ?? cur.title,
        date: pg.date ?? cur.date,
        type: pg.type ?? cur.type,
        posts: stabilizeMergedPlannerPostsOrder(cur.posts, byPostId, incomingPosts),
      }
    }
  })

  let finalMerged = merged
  if (pruneStalePipelineGroups && Array.isArray(pipelineGroups)) {
    const seenSources = new Set(
      pipelineGroups.map((pg) => String(pg?.sourceEventId ?? "").trim()).filter(Boolean),
    )
    finalMerged = merged.filter((g) => {
      if (!isPipelineTopicGroupId(g?.id)) return true
      const sid = String(g?.sourceEventId ?? "").trim()
      return sid !== "" && seenSources.has(sid)
    })
  }

  const dateR =
    Array.isArray(dateRange) && dateRange.length === 2
      ? dateRange
      : persisted?.dateRange?.length === 2 && persisted.clientId === clientId
        ? persisted.dateRange
        : dateRange

  return {
    clientId,
    dateRange: dateR,
    generatedEventGroups: serializeGeneratedGroupsForSession(finalMerged),
    scheduledGeneratedPostIds: [...(persisted?.scheduledGeneratedPostIds || [])],
    draftGeneratedPostIds: [...(persisted?.draftGeneratedPostIds || [])],
    selectedEventIds: [...(persisted?.selectedEventIds || [])],
    selectedPillarIds: [...(persisted?.selectedPillarIds || [])],
    selectedChannelIds: [...(persisted?.selectedChannelIds || [])],
    selectedPublishingStyleIds: [...(persisted?.selectedPublishingStyleIds || [])],
    mediaAssets: persisted?.mediaAssets || [],
  }
}

/** Substrings returned when caption / reel generation failed (API copy). Matched case-insensitively. */
const AI_PLANNER_MEDIA_FAILURE_CAPTION_FRAGMENTS = [
  "can't generate this caption",
  "cannot generate this caption",
  "couldn't generate this caption",
  "could not generate this caption",
  "unable to generate this caption",
  "unable to generate a caption",
  "no image media is available",
  "no image media available",
  "no relevant video",
  "reel not created",
  "no video is available",
  "no video available",
]

export function generatedPostHasRenderableMedia(post) {
  const m = post?.media
  if (!m) return false
  const src = typeof m.src === "string" ? m.src.trim() : ""
  const previewSrc = typeof m.previewSrc === "string" ? m.previewSrc.trim() : ""
  return Boolean(src || previewSrc)
}

function normalizedCaptionForFailureMatch(text) {
  return String(text ?? "")
    .trim()
    .toLowerCase()
    .replace(/\u2018|\u2019|\u201b/g, "'")
}

function generatedPostCaptionLooksLikeMediaOrCaptionFailure(text) {
  const raw = String(text ?? "").trim()
  if (!raw) return true
  const t = normalizedCaptionForFailureMatch(raw)
  return AI_PLANNER_MEDIA_FAILURE_CAPTION_FRAGMENTS.some((frag) => t.includes(frag))
}

/**
 * Pipeline posts with no image/video URL and only an empty or error caption (e.g. missing media for style,
 * reel skipped with no relevant video). These are hidden from the planner results grid.
 */
export function shouldHideUnusableAiPlannerGeneratedPost(post) {
  if (generatedPostHasRenderableMedia(post)) return false
  return generatedPostCaptionLooksLikeMediaOrCaptionFailure(post?.text)
}

export function filterAiPlannerEventGroupsForDisplay(groups) {
  if (!Array.isArray(groups)) return []
  return groups
    .map((g) => ({
      ...g,
      posts: (g.posts || []).filter((p) => !shouldHideUnusableAiPlannerGeneratedPost(p)),
    }))
    .filter((g) => (g.posts || []).length > 0)
}
